/**
 * The status cluster at the right end of the top bar (web.md §4 "Additions"): an on-device model's
 * download progress (models.md §12), harvesting, syncing, offline, sync error and the storage warning —
 * each a small icon with a tooltip, shown only while it
 * applies, so an idle workspace looks exactly like the prototype.
 */

import { useEffect, useState } from 'react';
import type { PipelineStatus, SyncStatus } from '@digester/core';
import { Icon } from '../icons/Icon.js';
import { useEngineQuery, useEngineStatus } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { percent } from '../settings/models/catalog.js';
import { useInstalls, useModels } from '../settings/models/data.js';

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

interface Signal {
  key: string;
  icon: string;
  label: string;
  tone?: 'warn' | 'busy';
  /** A short text beside the icon (a download's percentage). */
  text?: string;
}

/** An on-device model's download on this device (models.md §12: the status cluster shows its progress). */
export interface DownloadSignal {
  name: string;
  phase: 'downloading' | 'paused' | 'verifying';
  percent: number;
}

export function statusSignals(input: { online: boolean; durable: boolean | null; sync: SyncStatus | undefined; pipeline: PipelineStatus | undefined; downloads?: readonly DownloadSignal[] }): Signal[] {
  const out: Signal[] = [];
  for (const d of input.downloads ?? []) {
    const label = d.phase === 'verifying' ? `Verifying ${d.name}` : d.phase === 'paused' ? `${d.name} · download paused at ${d.percent} %` : `Downloading ${d.name} · ${d.percent} %`;
    out.push({ key: `model-download`, icon: d.phase === 'paused' ? 'pause' : 'download', label, ...(d.phase === 'paused' ? { tone: 'warn' as const } : {}), text: d.phase === 'verifying' ? 'checking' : `${d.percent} %` });
  }
  if (!input.online) out.push({ key: 'offline', icon: 'wifi-off', label: 'Offline — changes are kept on this device', tone: 'warn' });
  if (input.pipeline?.running) out.push({ key: 'harvesting', icon: 'loader', label: 'Harvesting sources…', tone: 'busy' });
  const phase = input.sync?.phase;
  if (phase === 'pushing' || phase === 'pulling') out.push({ key: 'syncing', icon: 'refresh-cw', label: 'Syncing…', tone: 'busy' });
  if (phase === 'offline' && input.online) out.push({ key: 'sync-offline', icon: 'cloud-off', label: 'Sync is waiting for the server', tone: 'warn' });
  if (phase === 'error' || phase === 'resync') out.push({ key: 'sync-error', icon: 'circle-alert', label: input.sync?.error?.message ?? 'Sync needs attention', tone: 'warn' });
  if (input.durable === false) out.push({ key: 'storage', icon: 'hard-drive', label: 'This browser cannot keep data after you close the tab.', tone: 'warn' });
  return out;
}

export function StatusCluster() {
  const online = useOnline();
  const { durable } = useEngineStatus();
  const sync = useEngineQuery(qk.sync(), (e) => e.sync.status()).data;
  const pipeline = useEngineQuery(qk.pipeline(), (e) => e.pipeline.status()).data;
  const installs = useInstalls();
  const { models } = useModels();
  const downloads: DownloadSignal[] = [];
  for (const state of installs.byId.values()) {
    if (state.phase !== 'downloading' && state.phase !== 'paused' && state.phase !== 'verifying') continue;
    downloads.push({ name: models.find((m) => m.id === state.modelId)?.name ?? state.modelId, phase: state.phase, percent: percent(state) });
  }
  const signals = statusSignals({ online, durable, sync, pipeline, downloads });
  if (!signals.length) return null;
  return (
    <div className="status-cluster" role="status" aria-live="polite">
      {signals.map((s) => (
        <span key={s.key} className={[s.tone ? `status-signal ${s.tone}` : 'status-signal', s.text ? 'with-text' : ''].filter(Boolean).join(' ')} title={s.label} aria-label={s.label} data-status={s.key}>
          <Icon name={s.icon} />
          {s.text ? <small>{s.text}</small> : null}
        </span>
      ))}
    </div>
  );
}
